import { useState, useEffect, useCallback } from 'react';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';

/**
 * useNotifications - Hook to manage in-app notifications
 * 
 * In DEMO MODE: Returns empty notifications, skips all API calls
 */
const useNotifications = () => {
  const { isDemoMode } = useDemo();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    // Skip in demo mode
    if (isDemoMode) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await API.get('/notifications');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [isDemoMode]);

  const fetchUnreadCount = useCallback(async () => {
    // Skip in demo mode
    if (isDemoMode) return;
    
    try {
      const response = await API.get('/notifications/unread-count');
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [isDemoMode]);

  const markAsRead = useCallback(async (notificationId) => {
    // Skip in demo mode
    if (isDemoMode) return;
    
    try {
      await API.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [isDemoMode]);

  const markAllAsRead = useCallback(async () => {
    // Skip in demo mode
    if (isDemoMode) return;
    
    try {
      await API.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, [isDemoMode]);

  const deleteNotification = useCallback(async (notificationId) => {
    // Skip in demo mode
    if (isDemoMode) return;
    
    try {
      await API.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, [isDemoMode, fetchUnreadCount]);

  const clearAll = useCallback(async () => {
    // Skip in demo mode
    if (isDemoMode) return;
    
    try {
      await API.delete('/notifications/clear-all');
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  }, [isDemoMode]);

  // Initial fetch
  useEffect(() => {
    // Skip in demo mode
    if (isDemoMode) {
      setLoading(false);
      return;
    }
    fetchNotifications();
  }, [isDemoMode, fetchNotifications]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    // Skip in demo mode
    if (isDemoMode) return;
    
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isDemoMode, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    refetch: fetchNotifications,
  };
};

export default useNotifications;
